import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Calendar, Flame, Palette, Trophy, Store, Sparkles, Camera, Activity, Heart, Check, Star, TrendingUp, Music, Utensils, PenTool, Dumbbell } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { PrimaryButton, GhostButton } from "@/components/ui-bits";

const FLYWHEEL = [
  { w: "Passion", d: "Discover what moves you", to: "/explore" },
  { w: "People", d: "Find your kind of humans", to: "/communities" },
  { w: "Community", d: "Belong to a space", to: "/communities" },
  { w: "Activity", d: "Show up at events", to: "/events" },
  { w: "Creation", d: "Make & showcase", to: "/challenges" },
  { w: "Reputation", d: "Grow your journey", to: "/me" },
  { w: "Opportunity", d: "Turn it into income", to: "/marketplace" },
];
const HERO_IMG = "https://images.unsplash.com/photo-1683200107031-e720a3fddf57?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STEPS = [
  { icon: Sparkles, t: "Discover your passion", d: "Pick what moves you — photography, fitness, art and more to come.", c: "#FF6B00", c2: "#FFC94A" },
  { icon: Users, t: "Find your people", d: "Join communities of makers, doers and beginners just like you.", c: "#55708F", c2: "#8FAF9B" },
  { icon: Calendar, t: "Show up & participate", d: "Join photo walks, runs and workshops. Take on challenges together.", c: "#8FAF9B", c2: "#55708F" },
  { icon: Palette, t: "Create & showcase", d: "Share projects, build a portfolio and get honest, kind feedback.", c: "#FF6B00", c2: "#8FAF9B" },
  { icon: Trophy, t: "Build reputation", d: "Earn achievements and grow a meaningful record of your journey.", c: "#FFC94A", c2: "#FF6B00" },
  { icon: Store, t: "Turn it into opportunity", d: "Sell creations, offer services and monetize your community.", c: "#8FAF9B", c2: "#FFC94A" },
];

const PILLARS = [
  { icon: Users, t: "Communities", d: "The heart of PassionAte. Browse, join or build a space around any passion.", c: "#FF6B00", c2: "#FFC94A" },
  { icon: Calendar, t: "Events", d: "Real meetups, online workshops and local experiences you can actually attend.", c: "#8FAF9B", c2: "#55708F" },
  { icon: Flame, t: "Challenges", d: "Recurring loops that keep you creating — 30-day streaks, community voting and prizes.", c: "#FF6B00", c2: "#8FAF9B" },
  { icon: Palette, t: "Projects", d: "A portfolio for your passion. Document what you make and how you grow.", c: "#55708F", c2: "#8FAF9B" },
  { icon: Store, t: "Marketplace", d: "Creations, hobby products and services — turn skill into income.", c: "#FFC94A", c2: "#FF6B00" },
  { icon: Trophy, t: "Reputation", d: "Achievements and a visual passion journey that celebrate participation.", c: "#8FAF9B", c2: "#FFC94A" },
];

const NAV_LINKS = [
  { label: "Our Journey", href: "#journey" },
  { label: "Why PassionAte", href: "#why" },
  { label: "How it works", href: "#how" },
  { label: "For builders", href: "#builders" },
];

function Nav() {
  return (
    <header className="sticky top-0 z-40 bg-[#FFFDF8]/70 backdrop-blur-xl border-b border-[#071126]/8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
        <Logo />
        <nav className="hidden lg:flex flex-nowrap items-center gap-1 mx-auto">
          {NAV_LINKS.map((n) => (
            <a key={n.href} href={n.href} data-testid={`nav-link-${n.href.replace("#", "")}`} className="whitespace-nowrap px-3.5 py-2 rounded-full text-sm font-medium text-[#071126]/70 hover:text-[#FF6B00] hover:bg-[#FF6B00]/8 transition-colors">{n.label}</a>
          ))}
        </nav>
        <div className="ml-auto md:ml-0 flex items-center gap-3">
          <GhostButton as={Link} to="/login" data-testid="landing-login" className="hidden sm:inline-flex">Log in</GhostButton>
          <PrimaryButton as={Link} to="/signup" data-testid="landing-signup">Get started<ArrowRight className="h-4 w-4" /></PrimaryButton>
        </div>
      </div>
    </header>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <span className="block text-3xl font-extrabold text-[#071126]">{value}</span>
      <span className="text-sm text-[#071126]/55">{label}</span>
    </div>
  );
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const n = (k, fallback) => (stats && stats[k] != null ? stats[k] : fallback);

  return (
    <div className="bg-[#FFFDF8] text-[#071126]">
      <Nav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="pa-in flex flex-col items-center text-center">
          <span className="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FF6B00]/10 text-[#c25400] text-xs font-semibold mb-6 cursor-default transition-all duration-300 hover:bg-[#FF6B00]/20 hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(255,107,0,0.25)]">
            <Heart className="h-3.5 w-3.5 pa-beat fill-[#FF6B00] text-[#FF6B00]" /> Turning Passion into Prosperity
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.12]">
            Find Your People.<br />Build Your Passion.<br /><span className="text-[#FF6B00]">Create Your Opportunity.</span>
          </h1>
          <p className="text-lg text-[#071126]/65 mt-6 max-w-xl leading-relaxed">
            PassionAte brings people, communities, experiences, and opportunities around your passion into one place.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <PrimaryButton as={Link} to="/signup" data-testid="hero-explore-cta" className="text-base px-6 py-3">Explore Passions<ArrowRight className="h-4 w-4" /></PrimaryButton>
            <GhostButton as={Link} to="/signup" data-testid="hero-create-cta" className="text-base px-6 py-3">Create a Community</GhostButton>
          </div>
          <div className="flex items-center justify-center gap-6 mt-10">
            <Stat value={n("passions", 3)} label="passions" />
            <div className="h-8 w-px bg-[#071126]/10" />
            <Stat value={n("communities", 9)} label="communities" />
            <div className="h-8 w-px bg-[#071126]/10" />
            <Stat value={`${n("members", 40)}+`} label="members" />
          </div>
        </div>
        <div className="relative pa-in lg:pl-8" style={{ animationDelay: ".1s" }}>
          <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(7,17,38,0.18)] rotate-1">
            <img src={HERO_IMG} alt="A passionate community together" className="w-full h-[440px] object-cover" />
          </div>
          {/* floating hobby icons */}
          {[
            { Icon: Camera, cls: "-top-5 left-6 bg-[#55708F]", d: "0s", r: "-8deg" },
            { Icon: Music, cls: "top-16 -left-6 bg-[#FF6B00]", d: ".6s", r: "6deg" },
            { Icon: Dumbbell, cls: "bottom-24 -left-7 bg-[#8FAF9B]", d: "1.2s", r: "-5deg" },
            { Icon: Palette, cls: "-bottom-4 left-24 bg-[#FFC94A]", d: ".9s", r: "8deg" },
            { Icon: Utensils, cls: "top-24 -right-6 bg-[#FF6B00]", d: ".3s", r: "10deg" },
            { Icon: PenTool, cls: "bottom-16 -right-5 bg-[#55708F]", d: "1.5s", r: "-7deg" },
          ].map(({ Icon, cls, d, r }, i) => (
            <span key={i} className={`absolute ${cls} h-12 w-12 rounded-2xl grid place-items-center text-white shadow-xl pa-float`} style={{ animationDelay: d, "--r": r }}>
              <Icon className="h-5 w-5" />
            </span>
          ))}
          <div className="absolute -bottom-5 right-6 bg-white rounded-2xl shadow-xl p-3.5 flex items-center gap-3 rotate-2">
            <span className="h-9 w-9 rounded-xl bg-[#FF6B00]/12 grid place-items-center"><Trophy className="h-4.5 w-4.5 text-[#FF6B00]" /></span>
            <div><p className="text-sm font-semibold">Achievement unlocked</p><p className="text-xs text-[#071126]/55">Consistent Creator</p></div>
          </div>
        </div>
      </section>

      {/* Passions strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="pa-card px-6 py-5 flex flex-wrap items-center justify-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mr-1">Passions on PassionAte</span>
          {["Photography", "Fitness & Wellness", "Art & Creative", "Music", "Cooking", "Writing"].map((p) => (
            <span key={p} className="px-3.5 py-1.5 rounded-full bg-[#FFFDF8] border border-[#071126]/8 text-sm font-medium text-[#071126]/80">{p}</span>
          ))}
          <span className="px-3.5 py-1.5 rounded-full bg-[#FF6B00]/10 text-[#c25400] text-sm font-semibold">+ more soon</span>
        </div>
      </section>

      {/* Flywheel */}
      <section id="journey" className="bg-[#071126] py-16 overflow-hidden relative scroll-mt-16">
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 15% 20%, rgba(255,107,0,0.18), transparent 42%), radial-gradient(circle at 85% 80%, rgba(255,201,74,0.12), transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FFC94A] mb-3">The Passion Flywheel</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10">One journey, from spark to livelihood</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {FLYWHEEL.map((f, i) => (
              <Link
                key={f.w}
                to={f.to}
                data-testid={`flywheel-${f.w.toLowerCase()}`}
                className="group flex items-center gap-3 w-[calc(50%-0.375rem)] sm:w-[calc(33.333%-0.5rem)] lg:w-[calc(25%-0.5625rem)] rounded-2xl border border-white/12 bg-white/[0.05] backdrop-blur-sm px-4 py-3.5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FF6B00] hover:border-[#FF6B00] hover:shadow-[0_10px_28px_rgba(255,107,0,0.35)]"
              >
                <span className="h-8 w-8 flex-shrink-0 rounded-xl bg-[#FF6B00]/25 text-[#FFC94A] grid place-items-center text-sm font-bold transition-colors group-hover:bg-white/25 group-hover:text-white">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-white leading-tight">{f.w}</span>
                  <span className="block text-xs text-white/55 leading-tight transition-colors group-hover:text-white/90">{f.d}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="why" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">Why PassionAte</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Turn Your Passion Into Something More</h2>
          <p className="text-[#071126]/60 mt-3">No more scattering your hobby across five apps. Community first — learning is just a bonus.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Link to="/signup" key={p.t} className="pa-card pa-card-hover p-7 pa-in flex flex-col items-center text-center group active:scale-[0.98]" style={{ animationDelay: `${i * 0.05}s`, background: `linear-gradient(140deg, ${p.c}33 0%, ${p.c2}26 100%)` }} data-testid={`pillar-${p.t.toLowerCase()}`}>
              <span className="h-14 w-14 rounded-2xl grid place-items-center mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" style={{ background: `linear-gradient(135deg, ${p.c} 0%, ${p.c2} 100%)`, boxShadow: `0 8px 18px ${p.c}55` }}>
                <p.icon className="h-7 w-7 text-white" />
              </span>
              <h3 className="text-lg font-semibold">{p.t}</h3>
              <p className="text-[#071126]/60 mt-1.5 text-sm leading-relaxed">{p.d}</p>
              <span className="mt-3 text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Get started<ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white border-y border-[#071126]/8 py-16 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">From passion to opportunity in six steps</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => {
              return (
              <Link to="/signup" key={s.t} data-testid={`step-${i + 1}`} className="pa-card pa-card-hover p-7 h-full flex flex-col items-center text-center group active:scale-[0.98]" style={{ background: `linear-gradient(140deg, ${s.c}30 0%, ${s.c2}22 100%)` }}>
                <div className="relative mb-4">
                  <span className="h-14 w-14 rounded-2xl grid place-items-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" style={{ background: `linear-gradient(135deg, ${s.c} 0%, ${s.c2} 100%)`, boxShadow: `0 8px 18px ${s.c}55` }}>
                    <s.icon className="h-7 w-7 text-white" />
                  </span>
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#071126] text-white grid place-items-center font-bold text-xs shadow">{i + 1}</span>
                </div>
                <h3 className="font-semibold text-lg">{s.t}</h3>
                <p className="text-[#071126]/60 mt-1.5 text-sm">{s.d}</p>
              </Link>
            ); })}
          </div>
        </div>
      </section>

      {/* Builders */}
      <section id="builders" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center scroll-mt-20">
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-[0_24px_50px_rgba(7,17,38,0.14)] rotate-1">
            <img src="https://images.unsplash.com/photo-1563279699-4c5dfcab048c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Community builder hosting" className="w-full h-[400px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/45 to-transparent" />
          </div>
          <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 -rotate-2">
            <span className="h-10 w-10 rounded-xl bg-[#FF6B00]/12 grid place-items-center"><TrendingUp className="h-5 w-5 text-[#FF6B00]" /></span>
            <div><p className="text-sm font-semibold">+128 members</p><p className="text-xs text-[#071126]/55">this month</p></div>
          </div>
          <div className="absolute -bottom-5 -right-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 rotate-2">
            <span className="h-10 w-10 rounded-xl bg-[#8FAF9B]/20 grid place-items-center"><Star className="h-5 w-5 text-[#4d7361] fill-[#FFC94A]" /></span>
            <div><p className="text-sm font-semibold">First sale made</p><p className="text-xs text-[#071126]/55">Watercolor print</p></div>
          </div>
        </div>
        <div className="flex flex-col items-center text-center lg:items-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">For community builders</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Build a community.<br /><span className="text-[#FF6B00]">Grow a livelihood.</span></h2>
          <p className="text-[#071126]/65 mt-4 max-w-lg">Anyone can become a builder in a tap. Create your community, host paid events, run challenges, sell your creations and track it all from one dashboard.</p>
          <ul className="mt-6 space-y-3 text-left">
            {["Create communities & grow your members", "Host events and workshops", "Launch challenges that keep people coming back", "Sell creations, products and services"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-[#071126]/80"><span className="h-6 w-6 rounded-full bg-[#8FAF9B]/25 grid place-items-center flex-shrink-0"><Check className="h-3.5 w-3.5 text-[#4d7361]" /></span>{f}</li>
            ))}
          </ul>
          <PrimaryButton as={Link} to="/signup" className="mt-8 mx-auto" data-testid="builders-cta">Start building<ArrowRight className="h-4 w-4" /></PrimaryButton>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-[#FF6B00] px-8 py-14 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,201,74,0.4), transparent 45%)" }} />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Your passions are waiting.</h2>
            <p className="text-white/90 mt-4 max-w-xl mx-auto text-lg">Join {n("members", 40)}+ people across {n("communities", 9)} communities turning what they love into people, projects and opportunity.</p>
            <GhostButton as={Link} to="/signup" className="mt-8 bg-white text-[#071126] border-white text-base px-7 py-3.5" data-testid="final-cta">Get started free<ArrowRight className="h-4 w-4" /></GhostButton>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#071126]/8 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-[#071126]/50">Turning Passion into Prosperity · © 2026 PassionAte</p>
        </div>
      </footer>
    </div>
  );
}
